// @ts-check
/**
 * Tallowlove Club: the first-visit popup and the sign-up forms (popup + footer).
 * Sign-ups go through Shopify's customer form (Customers, with email marketing
 * consent); on success the club discount code is applied to the cart.
 * Mirrors tallowlove-stardust/src/scripts/app.ts ("club popup + forms").
 */
import { $, $$, config, openDialog, closeDialog } from '@theme/theme';

const KEY = 'tl_club';
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

/** @returns {{ s: 'joined' | 'dismissed', t: number } | null} */
function state() {
  try {
    return JSON.parse(localStorage.getItem(KEY) ?? 'null');
  } catch {
    return null;
  }
}
/** @param {'joined' | 'dismissed'} s */
function save(s) {
  try {
    localStorage.setItem(KEY, JSON.stringify({ s, t: Date.now() }));
  } catch {
    /* storage unavailable (private mode): the popup simply shows again next visit */
  }
}

async function applyClubCode() {
  if (!config.clubCode) return;
  try {
    const cart = await import('@theme/cart');
    await cart.applyCode(config.clubCode);
  } catch {
    /* the code can still be entered at checkout */
  }
}

class ClubPopup extends HTMLElement {
  connectedCallback() {
    /** @type {HTMLDialogElement | null} */
    this.dialog = this.querySelector('dialog');
    if (!this.dialog || config.designMode || this.hasAttribute('data-disabled')) return;
    const st = state();
    const snoozeMs = Number(this.dataset.snooze || 14) * 864e5;
    const due = !st || (st.s === 'dismissed' && Date.now() - st.t > snoozeMs);
    if (!due) return;

    let fired = false;
    const depth = Number(this.dataset.scroll || 35) / 100;
    const onScroll = () => {
      if (scrollY > innerHeight * depth) fire();
    };
    const fire = () => {
      if (fired) return;
      fired = true;
      removeEventListener('scroll', onScroll);
      // Never interrupt an open cart, menu or zoom view.
      if ($$('dialog[open]').length) return;
      openDialog(this.dialog);
    };
    setTimeout(fire, Number(this.dataset.delay || 3) * 1000);
    addEventListener('scroll', onScroll, { passive: true });
    this.dialog.addEventListener('close', () => {
      if (state()?.s !== 'joined') save('dismissed');
    });
  }
}

class ClubForm extends HTMLElement {
  connectedCallback() {
    /** @type {HTMLFormElement | null} */
    this.form = this.querySelector('form');
    if (!this.form) return;
    this.form.noValidate = true;
    // Shopify's storefront captcha binds on first focus: it captures form.submit, takes over the
    // submit event, and calls the captured submit once the visitor passes the check. Installing
    // our fetch-based submit first means a captcha-verified sign-up still completes in place.
    this.form.submit = () => {
      this.send();
    };
    this.form.addEventListener('submit', (e) => this.onSubmit(e));
    // Returning from a no-JS submit: the server rendered the success state.
    if (this.classList.contains('is-joined') && state()?.s !== 'joined') this.joined(false);
  }

  /** @param {SubmitEvent} e */
  onSubmit(e) {
    const form = /** @type {HTMLFormElement} */ (this.form);
    const email = /** @type {HTMLInputElement} */ ($('input[type="email"]', form));
    const err = $('[data-club-error]', form);
    if (!EMAIL.test(email.value.trim())) {
      e.preventDefault();
      e.stopImmediatePropagation(); // no captcha round-trip for an address we already know is wrong
      email.setAttribute('aria-invalid', 'true');
      if (err) err.textContent = config.strings.invalidEmail;
      email.focus();
      return;
    }
    email.removeAttribute('aria-invalid');
    if (err) err.textContent = '';
    // Captcha-protected: Shopify's handler runs next and calls form.submit() → send().
    if (form.dataset.hcaptchaBound || form.dataset.recaptchaBound) return;
    e.preventDefault();
    this.send();
  }

  async send() {
    const form = /** @type {HTMLFormElement} */ (this.form);
    const email = /** @type {HTMLInputElement} */ ($('input[type="email"]', form));
    const err = $('[data-club-error]', form);
    const btn = /** @type {HTMLButtonElement} */ ($('button[type="submit"]', form));
    const native = () => HTMLFormElement.prototype.submit.call(form);
    btn.disabled = true;
    btn.classList.add('is-loading');
    try {
      const res = await fetch(form.action, { method: 'POST', body: new FormData(form), headers: { Accept: 'text/html' } });
      const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
      const echoed = form.id ? doc.getElementById(form.id) : null;
      // A challenge redirect or verification page: a normal submit lets Shopify run it in the browser.
      if (/\/challenge/.test(res.url) || !echoed) return native();
      const serverError = echoed.querySelector('[data-club-error]')?.textContent?.trim();
      if (!res.ok || serverError) {
        email.setAttribute('aria-invalid', 'true');
        if (err) err.textContent = serverError || config.strings.invalidEmail;
        btn.disabled = false;
        btn.classList.remove('is-loading');
        return;
      }
      await this.joined(true);
    } catch {
      native();
    }
  }

  /** @param {boolean} focus */
  async joined(focus) {
    save('joined');
    if (this.dataset.apply === 'true') await applyClubCode();
    this.classList.add('is-joined');
    const ok = /** @type {HTMLElement | null} */ (this.querySelector('[data-club-success]'));
    ok?.removeAttribute('hidden');
    if (focus) ok?.focus();
  }
}

if (!customElements.get('club-popup')) customElements.define('club-popup', ClubPopup);
if (!customElements.get('club-form')) customElements.define('club-form', ClubForm);

// Theme editor: selecting the popup section previews it.
document.addEventListener('shopify:section:select', (e) => {
  const dialog = /** @type {HTMLElement} */ (e.target).querySelector('club-popup dialog');
  if (dialog) openDialog(/** @type {HTMLDialogElement} */ (dialog));
});
document.addEventListener('shopify:section:deselect', (e) => {
  const dialog = /** @type {HTMLElement} */ (e.target).querySelector('club-popup dialog');
  if (dialog) closeDialog(/** @type {HTMLDialogElement} */ (dialog), { instant: true });
});
